<template>
  <div class="dpf" ref="wrapper">
    <div v-if="!value" class="dpf__search-wrapper">
      <ExTextInput
        :value="query"
        :ghostText="resolvedPlaceholder"
        icon="interface_search"
        :hideFloatingLabel="true"
        @input="onInput"
      />

      <div
        v-if="!browseList && query"
        class="dpf__dropdown"
        :class="{ 'dpf__list--readonly': readonly }"
        role="listbox"
      >
        <DepartmentPickerResultsList
          :departments="departments"
          :loading="browsePanelLoading"
          :browse-list="false"
          :has-query="true"
          @select="selectDepartment"
        />
      </div>
    </div>

    <div v-if="value && !query" class="dpf__picked">
      <Icon name="department_picker_icon" class="dpf__picked-icon" />
      <div class="dpf__breadcrumb">
        <span
          v-for="(seg, i) in breadcrumbSegments"
          :key="i"
          class="dpf__breadcrumb-item"
        >
          <span :class="i === breadcrumbSegments.length - 1 ? 'dpf__breadcrumb-leaf' : 'dpf__breadcrumb-ancestor'">{{ seg }}</span>
          <span v-if="i < breadcrumbSegments.length - 1" class="dpf__breadcrumb-sep">›</span>
        </span>
      </div>
      <span class="dpf__code">#{{ value.code }}</span>
      <div class="dpf__clear" @click.stop="clearSelection" role="button" aria-label="Clear selection">
        <Icon name="x_close_icon" />
      </div>
    </div>

    <div
      v-if="browseList && showBrowsePanel"
      class="dpf__browse-panel"
      :class="{ 'dpf__list--readonly': readonly }"
      role="listbox"
    >
      <DepartmentPickerResultsList
        :departments="visibleDepartments"
        :loading="browsePanelLoading"
        :browse-list="true"
        :has-query="Boolean(query)"
        @select="selectDepartment"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import Icon from "@components/icons/Icon.vue";
import DepartmentPickerResultsList from "./DepartmentPickerResultsList.vue";
import { orderDepartmentsTree } from "./departmentPickerFieldUtils.js";

const props = defineProps({
  value: { type: Object, default: null },
  departments: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  browseList: { type: Boolean, default: false },
  browseListRequiresQuery: { type: Boolean, default: false },
  searchPlaceholder: { type: String, default: "" },
});

const emit = defineEmits(["input", "search", "clear"]);

const query = ref("");
const isTyping = ref(false);
const browseLoadSettled = ref(false);
const wrapper = ref(null);
const debounceTimer = ref(null);

const resolvedPlaceholder = computed(
  () => props.searchPlaceholder || "Search departments by name or code"
);

const showBrowsePanel = computed(() => {
  if (!props.browseList) return false;
  if (props.browseListRequiresQuery) return Boolean(query.value);
  return !props.value || Boolean(query.value);
});

const visibleDepartments = computed(() => {
  if (props.browseList && !query.value) {
    return orderDepartmentsTree(props.departments);
  }
  return props.departments;
});

const browsePanelLoading = computed(() => {
  if (!props.browseList || query.value) {
    return isTyping.value || props.loading;
  }
  if (isTyping.value || props.loading) return true;
  if (!browseLoadSettled.value && props.departments.length === 0) return true;
  return false;
});

watch(
  () => props.departments.length,
  (length) => {
    if (props.browseList && length > 0) {
      browseLoadSettled.value = true;
    }
  }
);

watch(
  () => props.loading,
  (isLoading, wasLoading) => {
    if (props.browseList && wasLoading && !isLoading) {
      browseLoadSettled.value = true;
    }
  }
);

const breadcrumbSegments = computed(() => {
  if (!props.value) return [];
  const ancestors = Array.isArray(props.value.path) ? props.value.path.map((p) => p.name) : [];
  return [...ancestors, props.value.name];
});

function onInput(value) {
  query.value = value;
  isTyping.value = true;
  clearTimeout(debounceTimer.value);
  debounceTimer.value = setTimeout(() => {
    isTyping.value = false;
    emit("search", query.value);
  }, 300);
}

function selectDepartment(dept) {
  if (props.readonly) return;
  query.value = "";
  isTyping.value = false;
  emit("input", dept);
}

function clearSelection() {
  emit("input", null);
  emit("clear");
}

function clearQuery() {
  if (!query.value) return;
  query.value = "";
  isTyping.value = false;
  emit("search", "");
}

function handleOutsideClick(e) {
  if (props.browseList) return;
  if (wrapper.value && !wrapper.value.contains(e.target)) {
    clearQuery();
  }
}

onMounted(() => {
  document.addEventListener("click", handleOutsideClick);
  if (props.browseList && !props.browseListRequiresQuery) {
    emit("search", "");
  }
});

onUnmounted(() => {
  document.removeEventListener("click", handleOutsideClick);
  clearTimeout(debounceTimer.value);
});
</script>

<style scoped>
.dpf {
  width: 100%;
  font-family: Inter, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dpf__search-wrapper {
  position: relative;
  width: 100%;
}

.dpf__dropdown {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: 10;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 6px -2px rgba(16, 24, 40, 0.03), 0 12px 16px -4px rgba(16, 24, 40, 0.08);
}

.dpf__browse-panel {
  width: 100%;
  margin-top: 8px;
  background: transparent;
  border: 0;
  box-sizing: border-box;
}

/* Drop-target-only usage: suppress click/selection affordance on the result rows.
   Scoped to the listboxes so the search input and drop events are unaffected. */
.dpf__list--readonly {
  cursor: default;
  pointer-events: none;
}

.dpf__picked {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px 10px 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
}

.dpf__picked-icon {
  flex-shrink: 0;
}

.dpf__breadcrumb {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  font-size: 14px;
  line-height: 20px;
}

.dpf__breadcrumb-item {
  display: inline-flex;
  align-items: center;
}

.dpf__breadcrumb-ancestor {
  color: #475467;
  font-weight: 400;
}

.dpf__breadcrumb-sep {
  color: #d0d5dd;
  font-weight: 400;
  margin: 0 4px;
}

.dpf__breadcrumb-leaf {
  color: #6941c6;
  font-weight: 600;
}

.dpf__code {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 400;
  color: #475467;
  line-height: 20px;
  white-space: nowrap;
}

.dpf__clear {
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #98a2b3;
  padding: 2px;
  border-radius: 4px;
}

.dpf__clear:hover {
  color: #667085;
  background: #f2f4f7;
}
</style>
